import React, { useState, useEffect, useRef, useCallback } from 'react';
import { VscTrash, VscCloudDownload, VscCloudUpload } from 'react-icons/vsc';
import { BsFileEarmark } from 'react-icons/bs';
import { getPersonalFiles, uploadPersonalFiles, deletePersonalFile } from '../../api/personalFiles';
import FilePrintButton from './FilePrintButton';
import './FileManager.css';

function formatSize(bytes) {
  if (!bytes) return '0 B';
  const k = 1024, sizes = ['B','KB','MB','GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

export default function PersonalFileManager({ folder, isActive, onOpenTab, role }) {
  const [files, setFiles]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [dragging, setDragging]   = useState(false);
  const fileInputRef = useRef(null);
  const loadedRef    = useRef(false);

  const load = useCallback(async () => {
    if (!folder) return;
    setLoading(true);
    try { setFiles(await getPersonalFiles(folder._id)); }
    catch {}
    setLoading(false);
  }, [folder?._id]);

  useEffect(() => {
    if (isActive || !loadedRef.current) { loadedRef.current = true; load(); }
  }, [isActive, load]);

  const handleUpload = async (fileList) => {
    if (!fileList?.length) return;
    setUploading(true); setUploadError('');
    try { await uploadPersonalFiles(folder._id, fileList); await load(); }
    catch (err) { setUploadError(err?.response?.data?.msg || 'Upload failed.'); }
    setUploading(false);
  };

  const handleDragOver  = (e) => { e.preventDefault(); setDragging(true); };
  const handleDragLeave = (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragging(false); };
  const handleDrop      = (e) => { e.preventDefault(); setDragging(false); handleUpload(e.dataTransfer.files); };

  const handleDelete = async (file) => {
    if (!confirm(`Delete "${file.filename}"?`)) return;
    await deletePersonalFile(file._id);
    setFiles(prev => prev.filter(f => f._id !== file._id));
  };

  const openFile = (file) => {
    const ext = file.filename.toLowerCase().split('.').pop();
    if (ext === 'pdf') {
      if (navigator.maxTouchPoints > 0) {
        const token = localStorage.getItem('kspro_token');
        window.open(`/api/personal-files/${file._id}/download?token=${encodeURIComponent(token)}`, '_blank');
        return;
      }
      if (onOpenTab) onOpenTab(`pdf-${file._id}`, file.filename, { type: 'pdf-viewer', file: { ...file, _isPersonal: true } });
    } else if (ext === 'txt')
      onOpenTab(`txt-${file._id}`, file.filename, { type: 'txt-viewer', file: { ...file, _isPersonal: true } });
    else
      downloadFile(file);
  };

  const downloadFile = async (file) => {
    const token = localStorage.getItem('kspro_token');
    const res = await fetch(`/api/personal-files/${file._id}/download`, { headers: { 'x-auth-token': token } });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = file.filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  };

  const totalSize = files.reduce((s, f) => s + (f.size || 0), 0);

  return (
    <div className={`fm-root${dragging ? ' fm-dragging' : ''}`}
      onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>

      <div className="fm-header">
        <span className="fm-title">{folder?.name}</span>
        <span className="fm-meta">{files.length} file{files.length !== 1 ? 's' : ''} · {formatSize(totalSize)}</span>
        <button className="btn-primary fm-upload-btn" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
          <VscCloudUpload size={14} style={{ marginRight: 5 }} />
          {uploading ? 'Uploading…' : 'Upload'}
        </button>
        <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }}
          onChange={e => { handleUpload(Array.from(e.target.files)); e.target.value = ''; }} />
      </div>

      {uploadError && <div className="fm-error">{uploadError}</div>}
      {dragging && <div className="fm-drop-overlay"><VscCloudUpload size={40} /><div>Drop files to upload</div></div>}

      {loading ? (
        <div className="fm-loading">Loading…</div>
      ) : files.length === 0 ? (
        <div className="fm-empty">
          <VscCloudUpload size={36} />
          <div>No files yet</div>
          <div className="fm-empty-hint">Click Upload or drag files here</div>
        </div>
      ) : (
        <div className="fm-table-wrap">
          <table className="fm-table">
            <thead>
              <tr><th>Name</th><th>Size</th><th>Uploaded</th><th></th></tr>
            </thead>
            <tbody>
              {files.map(file => (
                <tr key={file._id} className="fm-row">
                  <td className="fm-cell-name">
                    <div className="fm-name-wrap">
                      <BsFileEarmark size={13} style={{ flexShrink: 0, color: 'var(--text-muted)' }} />
                      <span className="fm-filename" title={file.filename} onClick={() => openFile(file)}>
                        {file.filename}
                      </span>
                    </div>
                  </td>
                  <td className="fm-cell-size">{formatSize(file.size)}</td>
                  <td className="fm-cell-date">{new Date(file.createdAt).toLocaleDateString()}</td>
                  <td className="fm-cell-actions">
                    {file.filename?.toLowerCase().endsWith('.pdf') && (
                      <FilePrintButton fileId={file._id} isPersonal={true} role={role} />
                    )}
                    <button className="fl-btn-icon" title="Download" onClick={() => downloadFile(file)}>
                      <VscCloudDownload size={14} />
                    </button>
                    <button className="fl-btn-icon fl-btn-del" title="Delete" onClick={() => handleDelete(file)}>
                      <VscTrash size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
